import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDTopThree from "components/MDTopThree";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { getTopPerformingProSupSerCom } from "actions/statsActions";

function TopPerformers() {
  const dispatch = useDispatch();

  const proSupSerComTopPerforming = useSelector(
    (state) => state.proSupSerComTopPerforming
  );
  const { topPerformingProSupSerCom } = proSupSerComTopPerforming;

  useEffect(() => {
    dispatch(getTopPerformingProSupSerCom());
  }, [dispatch]);

  const Supplier = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1}>
      <MDTypography
        display="block"
        variant="caption"
        color="text"
        textAlign="left"
        fontWeight="medium"
        mb={1}
      >
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  function renderProductRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        supplier: (
          <Link to={`/products/${item?._id}`}>
            <Supplier
              key={index}
              image={item?.supplier?.supplierLogo}
              name={item?.supplier?.supplierName}
              email={item?.supplier?.supplierEmail}
            />
          </Link>
        ),
        product: (
          <Job title={item?.productName} description={item?.productNumber} />
        ),
        position: (
          <MDBox>
            <MDTopThree position={index + 1} />
          </MDBox>
        ),
        service: (
          <Job
            title={item?.service?.service}
            description={item?.service?.mainService}
          />
        ),
        value: (
          <MDBox ml={-1}>
            <Chip
              label={`${item?.orderCount} units`}
              variant="outlined"
              color="warning"
              size="large"
            />
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  function renderSupplierRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        supplier: (
          <Link to={`/suppliers/${item?._id}`}>
            <Supplier
              key={index}
              image={item?.supplierLogo}
              name={item?.supplierName}
              email={item?.supplierEmail}
            />
          </Link>
        ),
        location: <Job title={item?.supplierAddress} description="" />,
        position: (
          <MDBox>
            <MDTopThree position={index + 1} />
          </MDBox>
        ),
        joined: (
          <Job title={moment(item?.created_at).fromNow()} description="" />
        ),
        value: (
          <MDBox ml={-1}>
            <Chip
              label={`${item?.orderCount} units`}
              variant="outlined"
              color="warning"
              size="large"
            />
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  function renderClientRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        client: (
          <Link to={`/clients/${item?._id}`}>
            <Supplier
              key={index}
              image={item?.profile_image}
              name={item?.name}
              email={item?.email}
            />
          </Link>
        ),
        age: (
          <Job
            title={`${moment().diff(
              item?.date_of_birth,
              "years",
              false
            )} years old`}
            description=""
          />
        ),
        position: (
          <MDBox>
            <MDTopThree position={index + 1} />
          </MDBox>
        ),
        joined: (
          <Job title={moment(item?.created_at).fromNow()} description="" />
        ),
        value: (
          <MDBox ml={-1}>
            <Chip
              label={`${item?.orderCount} units`}
              variant="outlined"
              color="warning"
              size="large"
            />
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  function renderServiceRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        service: (
          <Link to={`/services/${item?._id}`}>
            <Supplier key={index} image="" name={item?.service} email="" />
          </Link>
        ),
        mainService: <Job title={item?.mainService} description="" />,
        position: (
          <MDBox>
            <MDTopThree position={index + 1} />
          </MDBox>
        ),
        value: (
          <MDBox ml={-1}>
            <Chip
              label={`${item?.productCount} units`}
              variant="outlined"
              color="warning"
              size="large"
            />
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Top Performing Products
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: [
                      {
                        Header: "supplier",
                        accessor: "supplier",

                        align: "left",
                      },
                      {
                        Header: "product",
                        accessor: "product",
                        align: "left",
                      },
                      {
                        Header: "position",
                        accessor: "position",
                        align: "center",
                      },
                      {
                        Header: "service",
                        accessor: "service",
                        align: "center",
                      },
                      { Header: "value", accessor: "value", align: "center" },
                    ],

                    rows: renderProductRows(
                      topPerformingProSupSerCom?.topProducts
                    ),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Top Performing Suppliers
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: [
                      {
                        Header: "supplier",
                        accessor: "supplier",
                        align: "left",
                      },
                      {
                        Header: "location",
                        accessor: "location",
                        align: "left",
                      },
                      {
                        Header: "position",
                        accessor: "position",
                        align: "center",
                      },
                      { Header: "joined", accessor: "joined", align: "center" },
                      { Header: "value", accessor: "value", align: "center" },
                    ],

                    rows: renderSupplierRows(
                      topPerformingProSupSerCom?.topSuppliers
                    ),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Top Performing Clients
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: [
                      { Header: "client", accessor: "client", align: "left" },
                      {
                        Header: "age",
                        accessor: "age",
                        align: "left",
                      },
                      {
                        Header: "position",
                        accessor: "position",
                        align: "center",
                      },
                      { Header: "joined", accessor: "joined", align: "center" },
                      { Header: "value", accessor: "value", align: "center" },
                    ],

                    rows: renderClientRows(
                      topPerformingProSupSerCom?.topClients
                    ),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Top Performing Services
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: [
                      {
                        Header: "service",
                        accessor: "service",
                        align: "left",
                      },
                      {
                        Header: "main service",
                        accessor: "mainService",
                        align: "left",
                      },
                      {
                        Header: "position",
                        accessor: "position",
                        align: "center",
                      },
                      { Header: "value", accessor: "value", align: "center" },
                    ],

                    rows: renderServiceRows(
                      topPerformingProSupSerCom?.topServices
                    ),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TopPerformers;
